// First-time shop tutorial: persistence + step data + layout resolution.
//
// The overlay (ShopTutorial.tsx) is a pure consumer of this module:
// it reads SHOP_TUTORIAL_STEPS, resolves each step's selector for the
// current layout, and the gate in SoloPlayScreen reads/writes the
// "seen" flag here.

// === Persistence ===
//
// Mirrors the version-keyed pattern in ui/paperTint.ts, with one
// deliberate difference: on a version mismatch we return `false`
// (treat as unseen) WITHOUT writing, so bumping TUTORIAL_VERSION_CURRENT
// re-shows the refreshed tour on every device's next round-1 shop.
// Only markShopTutorialSeen() ever writes the "seen" bit.

const TUTORIAL_SEEN_KEY = 'origami-shop-tutorial-seen'
const TUTORIAL_VERSION_KEY = 'origami-shop-tutorial-version'
/** Bump when the tutorial content changes enough to re-show it to
 *  everyone (new steps, reworked copy, restructured shop). */
const TUTORIAL_VERSION_CURRENT = '1'

/** sessionStorage (not localStorage): a one-shot "force-open on the
 *  next solo shop" request set by the Settings replay button. It must
 *  survive the soft-nav to ?solo but not leak across tabs/sessions. */
const REPLAY_REQUEST_KEY = 'origami-shop-tutorial-replay'

/** True only once the player has seen the tutorial under the current
 *  version. Drives the AUTO first-time trigger; replay paths ignore it. */
export function hasSeenShopTutorial(): boolean {
  if (typeof window === 'undefined') return true
  const savedVersion = window.localStorage.getItem(TUTORIAL_VERSION_KEY)
  if (savedVersion !== TUTORIAL_VERSION_CURRENT) return false
  return window.localStorage.getItem(TUTORIAL_SEEN_KEY) === '1'
}

/** Stamp the tutorial as seen under the current version. Called on
 *  every close (done / skip / manual) so the auto-trigger is strictly
 *  one-shot. */
export function markShopTutorialSeen(): void {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(TUTORIAL_VERSION_KEY, TUTORIAL_VERSION_CURRENT)
  window.localStorage.setItem(TUTORIAL_SEEN_KEY, '1')
}

/** Clear the seen flag so the tutorial would auto-show again. Used by
 *  the Settings "Replay" button alongside requestTutorialReplay(). */
export function resetShopTutorial(): void {
  if (typeof window === 'undefined') return
  window.localStorage.removeItem(TUTORIAL_SEEN_KEY)
}

/** Ask SoloPlayScreen to force-open the tutorial the next time a shop
 *  mounts (survives the ?solo navigation). */
export function requestTutorialReplay(): void {
  if (typeof window === 'undefined') return
  window.sessionStorage.setItem(REPLAY_REQUEST_KEY, '1')
}

/** Read-and-clear the force-open request. */
export function consumeTutorialReplayRequest(): boolean {
  if (typeof window === 'undefined') return false
  const had = window.sessionStorage.getItem(REPLAY_REQUEST_KEY) === '1'
  if (had) window.sessionStorage.removeItem(REPLAY_REQUEST_KEY)
  return had
}

// === Step data ===

export type ShopLayout = 'desktop' | 'mobile' | 'fold'
export type Placement = 'top' | 'bottom' | 'left' | 'right' | 'center'

export interface TutorialStep {
  id: string
  title: string
  /** Body copy — plain and concise, kept under ~140 chars. */
  body: string
  /** A universal CSS selector, or a per-layout map. `null` in a layout
   *  (or an unresolved selector at runtime) → centered bubble, no
   *  spotlight; the step still shows because its copy stands alone. */
  selector: string | Partial<Record<ShopLayout, string | null>>
  /** Preferred bubble side; the overlay auto-flips / clamps to fit. */
  placement?: Placement | Partial<Record<ShopLayout, Placement>>
}

/** Resolve a step's concrete selector + placement for the active layout. */
export function resolveStepTarget(
  step: TutorialStep,
  layout: ShopLayout,
): { selector: string | null; placement: Placement } {
  const selector =
    typeof step.selector === 'string'
      ? step.selector
      : step.selector[layout] ?? null
  const placement =
    typeof step.placement === 'string'
      ? step.placement
      : step.placement?.[layout] ?? 'bottom'
  return { selector, placement }
}

// `.shop-card` is rendered by the shared ShopCard component in all three
// layouts, so the first one is a universal anchor for the card steps.
const FIRST_CARD = '.shop-card'

export const SHOP_TUTORIAL_STEPS: TutorialStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to the shop',
    body: "Before each battle you draft a team here. Here's a quick tour — you can skip anytime.",
    selector: { desktop: null, mobile: null, fold: null },
    placement: 'center',
  },
  {
    id: 'shop-row',
    title: 'Units for sale',
    body: 'Each round offers a fresh set of units. Drag one onto your board below to buy it.',
    selector: {
      desktop: '.shop-grid__base',
      mobile: '.sh-m-drafting__row',
      fold: '.sh-fold-drafting__row',
    },
    placement: 'bottom',
  },
  {
    id: 'card-top',
    title: 'Tier, animal & element',
    body: 'Top-right numeral is the unit’s tier (power). The left disc shows its animal and element — matching them builds synergies.',
    selector: FIRST_CARD,
    placement: 'right',
  },
  {
    id: 'card-stats',
    title: 'Attack, health & cost',
    body: 'The fist (bottom-right) is attack, the heart (bottom-left) is health. The gold pill (top-left) is what it costs to buy.',
    selector: FIRST_CARD,
    placement: 'right',
  },
  {
    id: 'lineup',
    title: 'Your lineup',
    body: 'Up to five units fight here, front to back. Drag to reorder — position decides who strikes first.',
    selector: {
      desktop: '.shop-board__col--active',
      mobile: '.sh-m-board__rail--lineup',
      fold: '.sh-fold-lineup__slots',
    },
    placement: 'top',
  },
  {
    id: 'bench',
    title: 'The bench',
    body: 'Reserves sit on the bench. They don’t fight, but you can swap them into the lineup between rounds.',
    selector: {
      desktop: '.shop-board__bench',
      mobile: '.sh-m-board__rail--bench',
      fold: '.sh-fold-bench',
    },
    placement: 'top',
  },
  {
    id: 'combine',
    title: 'Combine to star up',
    body: 'Buy three copies of the same unit to merge them into a stronger, starred version with a brand-new look.',
    selector: {
      desktop: '.shop-board__col--active',
      mobile: '.sh-m-board__rail--lineup',
      fold: '.sh-fold-lineup__slots',
    },
    placement: 'top',
  },
  {
    id: 'sell',
    title: 'Selling units',
    body: 'Select a unit on your board, then use the sell tag to refund it for gold and free up the slot.',
    // Selection-gated: the sell ticket slides in from off-screen only
    // when a unit is selected, so it's never a stable anchor — always a
    // centered bubble. (The off-screen guard in ShopTutorial also covers
    // this defensively.)
    selector: { desktop: null, mobile: null, fold: null },
    placement: 'center',
  },
  {
    id: 'synergies',
    title: 'Synergies',
    body: 'Fielding units that share an animal or element unlocks team-wide bonuses. Watch them light up here as you draft.',
    selector: {
      desktop: '.shop-synergies',
      mobile: '.sh-m-synergies-tray__handle',
      fold: '.sh-fold-synergy-strip',
    },
    placement: { desktop: 'left', mobile: 'left', fold: 'top' },
  },
  {
    id: 'ready',
    title: 'Reroll & ready up',
    body: 'Spend gold to reroll for new units. When your team is set, press Ready to send them into battle. Good luck!',
    selector: {
      desktop: '.shop-header__ready',
      mobile: '.sh-m-btn--ready',
      fold: '.sh-m-btn--ready',
    },
    placement: 'bottom',
  },
]
